import React, { useState } from "react";
import { Check, X, RotateCcw, ArrowRight, Music2 } from "lucide-react";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { Stave, Single, Beamed, pairDirections, fromMiddle, toClef, viewBox, ClefCtx } from "@/components/QuaverDraw";

const ROUND = 10;
const PITCHES = ["D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5", "G5"];
const pickP = (clef) => { const l = toClef(PITCHES, clef); return l[Math.floor(Math.random() * l.length)]; };
const steps = (p, clef) => Math.abs(fromMiddle(p, clef));
const stepWord = (k) => `${k} step${k === 1 ? "" : "s"}`;

// Worked examples shown before the quiz: 1 step below + 3 above (the far note decides) and 1 below + 1 above (either way).
const examples = (clef) => {
  const [a, e, c] = toClef(["A4", "E5", "C5"], clef).map((p) => p);
  return [
    { ps: [a, e], up: false, ok: true, note: `Correct: ${e[0]} is 3 steps from the middle line and ${a[0]} only 1, so ${e[0]} decides — stems down.` },
    { ps: [a, e], up: true, ok: false, note: `Wrong: this follows ${a[0]}, the note nearest the middle line.` },
    { ps: [a, c], up: true, ok: true, note: `${a[0]} and ${c[0]} are both 1 step away, so stems up is correct…` },
    { ps: [a, c], up: false, ok: true, note: "…and stems down is correct too. You choose." },
  ];
};

const Pair = ({ ps, up }) => {
  const clef = React.useContext(ClefCtx);
  return (
    <svg viewBox={viewBox(0, 14, 100, 142, clef)} className={`mx-auto h-32 text-foreground ${clef === "bass" ? "w-36" : "w-28"}`} role="img" aria-label={`Beamed quavers ${ps.join(" and ")}, stems ${up ? "up" : "down"}`}>
      <Stave /><Beamed xs={[34, 66]} ps={ps} up={up} />
    </svg>
  );
};

export const BeamDirectionDiagram = ({ clef = "treble" }) => (
  <ClefCtx.Provider value={clef}>
  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid={clef === "bass" ? "beam-direction-diagram-bass" : "beam-direction-diagram"}>
    {examples(clef).map((e, i) => (
      <figure key={i} className={`rounded-xl border-2 p-2 ${e.ok ? "border-emerald-500/60 bg-emerald-500/5" : "border-red-500/60 bg-red-500/5"}`} data-testid={`beam-example-${i}`}>
        <div className="flex justify-end">{e.ok ? <Check size={18} className="text-emerald-500" aria-label="Correct" /> : <X size={18} className="text-red-500" aria-label="Wrong" />}</div>
        <Pair ps={e.ps} up={e.up} />
        <figcaption className="mt-1 text-center text-[11px] leading-snug text-muted-foreground">{e.note}</figcaption>
      </figure>
    ))}
  </div>
  </ClefCtx.Provider>
);

// Bars of 4/4: each item is a crotchet ("q"), a single quaver ("e") or a beamed group of n quavers ([n]).
const GOOD = [["p", "p", "p", "p"], ["q", "p", "q", "p"], ["p", "q", "p", "q"], ["q", "q", "p", "p"], ["p", "p", "q", "q"]];
const BAD = [
  { bar: [[3], "e", "q", "q"], why: "Three quavers are beamed together, crossing into beat 2. Beam quavers in pairs, one beat each." },
  { bar: ["q", "e", [2], "e", "q"], why: "That beam starts halfway through beat 2 and crosses into beat 3. Each pair should start on the beat." },
  { bar: [[8]], why: "Never beam across the middle of a 4/4 bar (between beats 2 and 3). Group the quavers in beats." },
  { bar: ["e", [2], "e", "q", "q"], why: "The beam joins the '&' of beat 1 to beat 2. Each pair should fill one beat." },
];

const Bar = ({ bar, ps }) => {
  const clef = React.useContext(ClefCtx);
  let slot = 0, k = 0;
  const x = (s) => 14 + s * 22;
  const parts = bar.map((it, i) => {
    if (it === "p") it = [2];
    if (Array.isArray(it)) {
      const n = it[0], group = ps.slice(k, k + n), xs = group.map((_, j) => x(slot + j));
      const up = pairDirections(group, clef)[0] === "up";
      slot += n; k += n;
      return <Beamed key={i} xs={xs} ps={group} up={up} />;
    }
    const p = ps[k++], el = <Single key={i} x={x(slot)} p={p} quaver={it === "e"} />;
    slot += it === "q" ? 2 : 1;
    return el;
  });
  return (
    <svg viewBox={viewBox(0, 14, 200, 142, clef)} className="mx-auto h-32 w-full max-w-md text-foreground" role="img" aria-label="A bar of 4/4">
      <Stave x1={2} x2={196} /><line x1="196" x2="196" y1="60" y2="108" stroke="currentColor" strokeWidth="1.4" />{parts}
    </svg>
  );
};

const makeQuestion = (clef) => {
  if (Math.random() < 0.5) {
    let ps = [pickP(clef), pickP(clef)];
    while (ps[0] === ps[1]) ps = [pickP(clef), pickP(clef)];
    const right = pairDirections(ps, clef), dir = Math.random() < 0.5 ? "up" : "down", ok = right.includes(dir);
    const [a, b] = ps.map((p) => steps(p, clef));
    const far = a >= b ? ps[0] : ps[1], other = far === ps[0] ? ps[1] : ps[0];
    const which = `the ${fromMiddle(far, clef) > fromMiddle(other, clef) ? "higher" : "lower"} note, ${far[0]},`;
    const why = right.length === 2 ? `Both notes are the same distance from the middle line (${stepWord(a)}), so stems up or down are both correct.`
      : `${which[0].toUpperCase()}${which.slice(1)} is furthest from the middle line (${stepWord(Math.max(a, b))}), so it decides: stems ${right[0]}.`;
    return { kind: "pair", ps, dir, ok, why, ask: "Are the stems pointing the right way?" };
  }
  const good = Math.random() < 0.5;
  const item = good ? { bar: GOOD[Math.floor(Math.random() * GOOD.length)], why: "Each pair of quavers is beamed within one beat." } : BAD[Math.floor(Math.random() * BAD.length)];
  return { kind: "bar", bar: item.bar, ps: Array.from({ length: 8 }, () => pickP(clef)), ok: good, why: item.why, ask: "Is this 4/4 bar beamed correctly?" };
};

// Lesson 4 (treble) and Lesson 5 (bass): right or wrong? Beamed quaver stem directions and grouping in a 4/4 bar.
export const BeamedQuaverQuiz = ({ testId = "beam-quiz", clef = "treble" }) => {
  const bass = clef === "bass", makeQ = () => makeQuestion(clef);
  const [q, setQ] = useState(makeQ);
  const [n, setN] = useState(1);
  const [score, setScore] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [done, setDone] = useState(false);
  const pick = (saysRight) => {
    if (answer !== null) return;
    const ok = saysRight === q.ok;
    setAnswer(ok); playResult(ok);
    if (ok) setScore((s) => s + 1);
  };
  const next = () => {
    if (n === ROUND) { setDone(true); if (score === ROUND) playFanfare(0.2, bass ? "Beamed quaver quiz (bass clef)" : "Beamed quaver quiz"); return; }
    setQ(makeQ()); setN(n + 1); setAnswer(null);
  };
  const restart = () => { setQ(makeQ()); setN(1); setScore(0); setAnswer(null); setDone(false); };
  const btn = "inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-bold transition-colors disabled:opacity-60";

  return (
    <ClefCtx.Provider value={clef}>
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Music2 size={14} /> Beamed quaver quiz{bass ? " · bass clef" : ""}</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>{done ? `${ROUND} of ${ROUND}` : `Question ${n} of ${ROUND}`} · Score {score}</p>
      </div>
      {done ? (
        <div className="mt-4 text-center">
          <p className="text-base font-semibold" data-testid={`${testId}-result`}>{score === ROUND ? `Perfect! ${score} out of ${ROUND} — you earned a star.` : `You scored ${score} out of ${ROUND}.`}</p>
          <button type="button" onClick={restart} data-testid={`${testId}-restart`} className={`${btn} mt-3 bg-primary text-primary-foreground`}><RotateCcw size={14} /> Play again</button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm font-semibold" data-testid={`${testId}-question`}>{q.ask}</p>
          <div className={`mt-3 rounded-xl border-2 bg-background/60 p-2 ${answer === null ? "border-border" : answer ? "border-emerald-500" : "border-red-500"}`} data-testid={`${testId}-card`} data-kind={q.kind} data-correct={q.ok}>
            {q.kind === "pair" ? <Pair ps={q.ps} up={q.dir === "up"} /> : <Bar bar={q.bar} ps={q.ps} />}
          </div>
          {answer === null ? (
            <div className="mt-3 flex justify-center gap-3">
              <button type="button" onClick={() => pick(true)} data-testid={`${testId}-right`} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}><Check size={15} /> Right</button>
              <button type="button" onClick={() => pick(false)} data-testid={`${testId}-wrong`} className={`${btn} bg-red-600 text-white hover:bg-red-700`}><X size={15} /> Wrong</button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-feedback`}>
              <p className={`text-sm font-semibold ${answer ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                {answer ? "Correct! " : "Not quite. "}{q.ok ? "It's right. " : "It's wrong. "}{q.why}
              </p>
              <button type="button" onClick={next} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">
                {n === ROUND ? "See my score" : "Next"} <ArrowRight size={13} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </ClefCtx.Provider>
  );
};
